/* eslint-disable camelcase */
import React, { useState, useMemo, useCallback } from 'react'
import TextField from '../EditCollect/BasicFields/TextField'
import SelectUserGroupField from '../EditCollect/BasicFields/SelectUserGroupField'
import { ErrorAlert } from '../shared/Export'
import FileInput from '../shared/FileInput'

const DEFAULT_ERRORS = {}


const ResourceForm = ({ data, onSubmit, submitting = false, errors = DEFAULT_ERRORS, mode = 'edit' }) => {
  // Initialize values_attributes with existing text content
  const getInitialValuesAttributes = () => {
    const initialValuesAttributes = {}
    data.values.forEach(value => {
      if (value.variable.variable_type === 'text')
        initialValuesAttributes[value.id] = { content: value.content || '' }
    })
    return initialValuesAttributes
  }

  const [formData, setFormData] = useState({
    name: data.name || '',
    address: data.address || '',
    user_ids: data.user_ids || [],
    values_attributes: getInitialValuesAttributes()
  })
  const [files, setFiles] = useState({})
  const [filesToDestroy, setFilesToDestroy] = useState({})

  // Prepare values for form submission
  const prepareValuesAttributes = useCallback(() => {
    const valuesAttributes = {}

    data.values.forEach((value, index) => {
      valuesAttributes[index] = {
        id: value.id,
        variable_id: value.variable_id,
        content: formData.values_attributes[value.id]?.content || value.content || ''
      }

      // Handle file values
      if (['pdf', 'image'].includes(value.variable.variable_type)) {
        if (files[value.id])
          valuesAttributes[index].file = files[value.id]

        // Handle file deletion
        if (filesToDestroy[value.id])
          valuesAttributes[index].file = ''
      }
    })

    return valuesAttributes
  }, [formData.values_attributes, files, data.values, filesToDestroy])

  const handleSubmit = useCallback(event => {
    event.preventDefault()

    // Build FormData explicitly - KISS principle
    const submitData = new FormData()
    submitData.append('resource[name]', formData.name)

    if (data.has_address)
      submitData.append('resource[address]', formData.address || '')

    // Add user IDs
    if (formData.user_ids.length > 0) {
      formData.user_ids.forEach(userId => {
        submitData.append('resource[user_ids][]', userId)
      })
    } else
      submitData.append('resource[user_ids][]', '')

    // Add values attributes
    const valuesAttributes = prepareValuesAttributes()
    Object.keys(valuesAttributes).forEach(key => {
      const value = valuesAttributes[key]
      submitData.append(`resource[values_attributes][${key}][id]`, value.id)
      submitData.append(`resource[values_attributes][${key}][variable_id]`, value.variable_id)
      submitData.append(`resource[values_attributes][${key}][content]`, value.content || '')

      if (value.file)
        submitData.append(`resource[values_attributes][${key}][file]`, value.file)

      if (filesToDestroy[value.id])
        submitData.append(`resource[values_attributes][${key}][file]`, '')
    })

    onSubmit(submitData)
  }, [formData, data.has_address, files, prepareValuesAttributes, onSubmit, filesToDestroy])

  const handleInputChange = useCallback(event => {
    const { name, value } = event.target
    // Extract field from name attribute
    const field = name.replace('resource_', '')
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }, [])

  const handleValueChange = useCallback(valueId => event => {
    const { value: content } = event.target
    setFormData(prev => ({
      ...prev,
      values_attributes: {
        ...prev.values_attributes,
        [valueId]: { ...prev.values_attributes[valueId], content }
      }
    }))
  }, [])

  const handleFileChange = useCallback(valueId => file => {
    if (file) {
      setFiles(prev => ({ ...prev, [valueId]: file }))
      // Clear destroy flag when new file is selected
      setFormData(prev => ({
        ...prev,
        values_attributes: {
          ...prev.values_attributes,
          [valueId]: { ...prev.values_attributes[valueId], file: null }
        }
      }))
    } else {
      // File was cleared
      setFiles(prev => {
        const newFiles = { ...prev }
        delete newFiles[valueId]
        return newFiles
      })
    }
  }, [])

  const handleFileDelete = useCallback(valueId => () => {
    const value = data.values.find(val => val.id === valueId)

    // Clear any new file selection
    setFiles(prev => {
      const newFiles = { ...prev }
      delete newFiles[valueId]
      return newFiles
    })

    if (value?.file) {
      // Mark existing file for deletion
      setFormData(prev => ({
        ...prev,
        values_attributes: {
          ...prev.values_attributes,
          [valueId]: {
            ...prev.values_attributes[valueId],
            file: '',
            content: ''
          }
        }
      }))
      setFilesToDestroy(prev => ({
        ...prev,
        [valueId]: true
      }))
    } else {
      // If no existing file, just clear the form data
      setFormData(prev => ({
        ...prev,
        values_attributes: {
          ...prev.values_attributes,
          [valueId]: {
            ...prev.values_attributes[valueId],
            file: null,
            content: ''
          }
        }
      }))
    }
  }, [data.values])

  const handleOperatorsChange = useCallback(event => {
    const userIds = Array.from(event.target.selectedOptions, option => option.value)
    setFormData(prev => ({
      ...prev,
      user_ids: userIds
    }))
  }, [])

  // Helper function to get errors for nested values
  const getValueErrors = useCallback((valueId, field = 'content') => {
    // Check for nested errors from NestedFormValidator (values[id][field])
    if (errors.values?.[valueId]?.[field])
      return errors.values[valueId][field]

    // Also check for flat key format: errors["values[id]"]["field"]
    const flatKey = `values[${valueId}]`
    if (errors[flatKey]?.[field])
      return errors[flatKey][field]

    return null
  }, [errors])

  const textValues = useMemo(() =>
    data.values.filter(value => value.variable.variable_type === 'text'), [data.values])

  const fileValues = useMemo(() =>
    data.values.filter(value => ['pdf', 'image'].includes(value.variable.variable_type)), [data.values])

  const getFileDisplay = useCallback(valueId => {
    // Check for new file first
    if (files[valueId])
      return files[valueId].name

    // Check for existing file (not marked for deletion)
    const value = data.values.find(val => val.id === valueId)
    const fileAttachAttrs = formData.values_attributes[valueId]?.file
    if (value?.file && fileAttachAttrs !== '')
      return value.file.filename

    return ''
  }, [files, data.values, formData.values_attributes])

  const newClass = 'new_resource plausible-event-name=Resource+Created'
  const editClass = 'edit_resource plausible-event-name=Resource+Edited'
  const formClass = mode === 'new' ? newClass : editClass

  const submitText = mode === 'new' ? 'Créer la nouvelle ressource' : 'Sauvegarder les modifications'
  const buttonText = submitting ? 'Patientez...' : submitText

  return (
    <form
      className={formClass}
      onSubmit={handleSubmit}
    >
      {errors.base ? <ErrorAlert message={errors.base.join(', ')} /> : null}
      <div className="row">
        <div className="col-md-12">
          <TextField
            name="resource_name"
            label="Nom *"
            value={formData.name}
            errors={errors.name}
            required
            onChange={handleInputChange}
          />
          <SelectUserGroupField
            name="user_ids"
            label="Opérateurs"
            data={data.operators}
            value={formData.user_ids}
            multiple
            onChange={handleOperatorsChange}
          />
          {data.has_address ? (
            <TextField
              name="resource_address"
              label="Adresse"
              value={formData.address}
              errors={errors.address}
              onChange={handleInputChange}
            />
          ) : null}
          {textValues.length > 0 && (
            <>
              <h5>Variables</h5>
              {textValues.map(value => (
                <div key={value.id} className="nested-fields ml-3">
                  <TextField
                    name={`text-value-${value.id}`}
                    label={`Valeur pour ${value.variable.name}`}
                    value={formData.values_attributes[value.id]?.content || ''}
                    errors={getValueErrors(value.id, 'content')}
                    onChange={handleValueChange(value.id)}
                  />
                </div>
              ))}
            </>
          )}
          {fileValues.length > 0 && (
            <div className="mt-2">
              <h5 className="mb-2">Variables avec fichiers</h5>
              <p className="mb-2">
                Importez les fichiers attendus pour chaque variables.
              </p>
              <div className="p-2 alert-file-warning">
                <span>
                  <strong>Attention :</strong>
                  {' '}
                  {'Modifier ou supprimer un fichier dans une ressource peut avoir des '}
                  conséquences sur toutes les collectes où ce fichier apparaît.
                </span>
              </div>
              {fileValues.map(value => {
                const isMarkedForDeletion = filesToDestroy[value.id]
                const isPdf = value.variable.variable_type === 'pdf'

                return (
                  <div key={value.id} className="nested-fields ml-3 mt-3">
                    <div>
                      <label htmlFor={`file-value-${value.id}`}>
                        Valeur pour
                        {' '}
                        {value.variable.name}
                      </label>
                      <FileInput
                        id={`file-value-${value.id}`}
                        name={`file-value-${value.id}`}
                        accept={isPdf ? '.pdf' : 'image/png,image/jpeg,image/jpg'}
                        buttonClass="btn-success"
                        buttonText="Choisir un fichier"
                        disabled={false}
                        errors={getValueErrors(value.id, 'file')}
                        placeholder="Aucun fichier sélectionné"
                        value={getFileDisplay(value.id)}
                        onChange={handleFileChange(value.id)}
                        onDelete={handleFileDelete(value.id)}
                      />
                      {isMarkedForDeletion && value.file ? (
                        <small className="text-danger">
                          Ce fichier sera supprimé lors de l&apos;enregistrement
                        </small>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <div className="col-md-12 mt-4">
          <button
            type="submit"
            className="btn btn-primary float-right"
            disabled={submitting}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </form>
  )
}

export default ResourceForm
